/**
 * Body Scale Calculation for Proportional Rendering
 * Calculates body size in pixels for proportional stroke/joint sizing
 */

import type { PixelLandmark } from './mapToPixels';

// MediaPipe Pose landmark indices
const POSE_LANDMARKS = {
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26
} as const;

/**
 * Calculate body scale in pixels using landmark distances
 * Returns shoulder width, falling back to hip width, then sensible default
 */
export function bodyScalePx(landmarks: PixelLandmark[]): number {
  if (!landmarks || landmarks.length === 0) {
    return 120; // Sensible fallback
  }
  
  // Helper to check if landmark is valid and visible
  const isOk = (index: number) => {
    const lm = landmarks[index];
    return lm && lm.visibility > 0.5;
  };
  
  // Helper to calculate distance between two landmarks
  const distance = (aIndex: number, bIndex: number) => {
    const a = landmarks[aIndex];
    const b = landmarks[bIndex];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };
  
  // Try shoulder width first (most reliable for scale)
  if (isOk(POSE_LANDMARKS.LEFT_SHOULDER) && isOk(POSE_LANDMARKS.RIGHT_SHOULDER)) {
    const shoulderWidth = distance(POSE_LANDMARKS.LEFT_SHOULDER, POSE_LANDMARKS.RIGHT_SHOULDER);
    if (shoulderWidth > 20) { // Minimum reasonable shoulder width
      return shoulderWidth;
    }
  }
  
  // Fallback to hip width
  if (isOk(POSE_LANDMARKS.LEFT_HIP) && isOk(POSE_LANDMARKS.RIGHT_HIP)) {
    const hipWidth = distance(POSE_LANDMARKS.LEFT_HIP, POSE_LANDMARKS.RIGHT_HIP);
    if (hipWidth > 15) { // Minimum reasonable hip width
      return hipWidth;
    }
  }
  
  // Last resort: try elbow-to-elbow for arm span estimate
  if (isOk(POSE_LANDMARKS.LEFT_ELBOW) && isOk(POSE_LANDMARKS.RIGHT_ELBOW)) {
    const elbowSpan = distance(POSE_LANDMARKS.LEFT_ELBOW, POSE_LANDMARKS.RIGHT_ELBOW);
    if (elbowSpan > 30) {
      return elbowSpan * 0.6; // Estimate shoulder width from elbow span
    }
  }
  
  // Final fallback
  return 120;
}

/**
 * Calculate proportional stroke and joint sizes based on body scale
 */
export function calculateProportionalSizes(
  bodyScale: number,
  config: {
    baseStrokePx: number;
    baseJointPx: number;
    minStrokePx: number;
    maxStrokePx: number;
    minJointPx: number;
    maxJointPx: number;
  }
): { strokePx: number; jointPx: number } {
  // Scale factor relative to reference body size (120px)
  const scaleFactor = bodyScale / 120;
  
  // Calculate sizes with clamping
  const strokePx = Math.max(
    config.minStrokePx,
    Math.min(
      config.maxStrokePx,
      config.baseStrokePx * scaleFactor
    )
  );
  
  const jointPx = Math.max(
    config.minJointPx,
    Math.min(
      config.maxJointPx,
      config.baseJointPx * scaleFactor
    )
  );
  
  return { strokePx, jointPx };
}

/**
 * Get landmark visibility statistics for debugging
 */
export function getLandmarkStats(landmarks: PixelLandmark[]): {
  total: number;
  visible: number;
  averageVisibility: number;
} {
  if (!landmarks || landmarks.length === 0) {
    return { total: 0, visible: 0, averageVisibility: 0 };
  }
  
  const visible = landmarks.filter(lm => lm && lm.visibility > 0.5).length;
  const totalVisibility = landmarks.reduce((sum, lm) => sum + (lm?.visibility || 0), 0);
  const averageVisibility = totalVisibility / landmarks.length;
  
  return {
    total: landmarks.length,
    visible,
    averageVisibility
  };
}
